import { LiveLinkAnswer } from "@/components/license-details/cards/live-link-answer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { updateForeclosureCaseReview } from "@/hera-api/foreclosure-license-link-api";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import {
  applyForeclosureCaseReviewToCachedLists,
  caseNumberOverrideCachePatch,
} from "@/lib/foreclosure-case-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useState } from "react";

interface ForeclosureCaseNumberEditCellProps {
  row: GovernmentForeclosureCaseListItem;
  portal: "government" | "operator";
}

/**
 * Lets an operator (or government user, matching the OMT/AMS/registrant
 * party cells) type a manual case number, overriding whatever ATTOM loaded
 * for display. The underlying case identity (`county_fips` + the system
 * `case_number`) that joins, reviews, and live links key off is never
 * rewritten — only `override_case_number` on the review row changes.
 */
export function ForeclosureCaseNumberEditCell({
  row,
  portal,
}: ForeclosureCaseNumberEditCellProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const effectiveCaseNumber = row.effective_case_number ?? row.case_number;
  const isOverridden = row.is_case_number_overridden ?? false;
  const [draftValue, setDraftValue] = useState<string>(effectiveCaseNumber);

  const saveMutation = useMutation({
    mutationFn: async (nextValue: string | null) =>
      updateForeclosureCaseReview(portal, {
        countyFips: row.county_fips,
        caseNumber: row.case_number,
        overrideCaseNumber: nextValue,
      }),
    onSuccess: (review) => {
      applyForeclosureCaseReviewToCachedLists(
        queryClient,
        review,
        caseNumberOverrideCachePatch(row, review),
      );
      setOpen(false);
      toast({
        title: "Case number saved",
        variant: "secondary",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to save case number",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftValue(effectiveCaseNumber);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div
        className="flex items-center gap-1"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {row.case_number_url_template ? (
          <LiveLinkAnswer
            value={effectiveCaseNumber}
            urlTemplate={row.case_number_url_template}
            copyValueToClipboard={row.case_number_copy_to_clipboard ?? false}
            httpMethod={row.case_number_http_method}
            valueExtractRegex={row.case_number_value_extract_regex}
            requestFields={row.case_number_request_fields}
            browserHelperConfig={row.case_number_browser_helper_config}
          />
        ) : (
          <span>{effectiveCaseNumber}</span>
        )}
        {isOverridden ? (
          <span
            className="text-xs text-muted-foreground"
            title="Manually overridden; the system-loaded case number is kept separately."
          >
            (manual)
          </span>
        ) : null}
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label={`Edit case number for case ${row.case_number}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-72 space-y-3"
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        <Input
          type="text"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          aria-label="Manual case number"
          placeholder="Manual case number"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={saveMutation.isPending || !draftValue.trim()}
            onClick={() => saveMutation.mutate(draftValue)}
          >
            Save
          </Button>
          {isOverridden ? (
            <Button
              size="sm"
              variant="outline"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(null)}
            >
              Clear override
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
